import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '@/styles/neon-data-grid.css';
import {
  NeonButton,
  NeonCardStandard,
  NeonCheckboxSingle,
  NeonErrorPageContent,
  NeonIcon,
  NeonTooltip,
} from '@ps-refarch-ux/neon';
import { DATA_SLOT, NeonButtonTypes } from '@/constants/neon-constants';
import {
  getCourseCatalogBreadcrumbs,
  clearCourseCatalogFilterSession,
  COURSE_CATALOG_ICON,
  COURSE_CATALOG_ID,
  COURSE_CATALOG_TEXT,
  DEFAULT_COURSE_CATALOG_PAGE_SIZE,
  COURSE_CATALOG_PAGINATION_THRESHOLD,
  EMPTY_COURSE_CATALOG_ID,
  EMPTY_COURSE_CATALOG_TEXT,
  EXPORT_TOAST,
  EXPORT_CATALOG_DOWNLOAD_FILENAME,
  EXPORT_MAPPING_DOWNLOAD_FILENAME,
  EXPORT_INFO_TOAST_OPTIONS,
  EXPORT_ERROR_TOAST_OPTIONS,
  GRADE_OPTIONS,
  BULK_ACTION_OPTIONS,
  BULK_ACTION_TOAST,
  COURSE_STATUS_OPTIONS,
  DELETE_COURSE_TOAST,
  formatCourseDeletedSuccessHeading,
  COURSE_CATALOG_GRID_HEADER_CLASS,
  COURSE_CATALOG_GRID_HEADER_CLASS_STATIC,
} from '@/constants/course-catalog-constants';
import {
  tableColumnsStyle,
  sectionSpacingStyle,
} from '@/containers/SubjectAreas/subject-areas.styles';
import { EmptyState } from '@/components/EmptyState/empty-state';
import { SortableHeader } from '@/components/sortable-header';
import type { FilterField, FilterChip } from '@/components/DataGrid/filter-panel';
import { DataGrid } from '@/components/DataGrid/data-grid';
import {
  useGetCourseCatalog,
  useExportCourses,
  useBulkUpdateCourseStatus,
  useBulkUpdateSubjectArea,
  useBulkDeleteCourses,
  useDeleteCourse,
  useFetchAllCourseIds,
} from '@/repositories/course-repository';
import { BulkChangeStatusModal } from './bulk-change-status-modal';
import { BulkChangeSubjectAreaModal } from './bulk-change-subject-area-modal';
import { BulkDeleteCoursesModal } from './bulk-delete-courses-modal';
import { DeleteCourseModal } from './delete-course-modal';
import { CourseSchoolsMappingDrawer } from './course-schools-mapping-drawer';
import { CourseCatalogTruncatingField } from './course-catalog-truncating-field';
import { useGetSubjectAreas } from '@/repositories/subject-areas-repository';
import { useGetSchools } from '@/repositories/schools-repository';
import { useCourseCatalogFilters } from '@/hooks/use-course-catalog-filters';

import type { Course, CourseFilters, School } from '@/types';
import { CourseStatus } from '@/types';
import { AllCommunityModule, ModuleRegistry, type GridOptions } from 'ag-grid-community';
import type { CellStyle, ColDef, GridReadyEvent } from 'ag-grid-community';
import type { DataGridCheckboxColumn } from '@/components/DataGrid/data-grid';
import { NeonPagination } from '@ps-refarch-ux/neon';
import { EMPTY_STATE_CARD_STYLE } from '@/components/EmptyState/empty-state.styles';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import {
  getCpContextHighschoolId,
  getImportCoursesUrl,
  canExportCourseMappingFromCpContext,
  isCourseCatalogReadOnly,
} from '@/utils/cp-context';
import { findSubjectAreaByOptionValue, subjectAreaToOptionValue } from '@/utils/subject-area-utils';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import { usePageConfig } from '@/layout-templates';
import {
  skeletonContainerStyle,
  skeletonHeaderStyle,
  skeletonRowStyle,
  skeletonBarStyle,
} from '@/containers/PlanOfStudy/plans-of-study.styles';
import './course-catalog.styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const EXPORT_OPTIONS = [
  { value: 'catalog', label: 'Course catalog' },
  { value: 'mapping', label: 'Course mapping' },
];

function toSelectorSafeOptionId(value: string): string {
  return `sa_${Array.from(value)
    .map((char) => (/^[A-Za-z0-9_-]$/.test(char) ? char : `_${char.codePointAt(0)?.toString(16)}_`))
    .join('')}`;
}

function schoolsPayloadForMappingExport(schools: School[]): Array<{ nid: string; name: string }> {
  // Embedded Succeed: when `CP_CONTEXT.HIGHSCHOOL_ID` is set (school-scoped staff), the mapping
  // export POST must send only that `{ nid, name }`; otherwise send every school from `/highschools`.
  const scopedNid = getCpContextHighschoolId();
  if (scopedNid) {
    const match = schools.find((s) => s.nid === scopedNid);
    return [{ nid: scopedNid, name: match?.name?.trim() ? match.name.trim() : scopedNid }];
  }
  return schools.map(({ nid, name }) => ({ nid, name }));
}

/**
 * True if `event` originated inside `container` (including shadow roots).
 * In Succeed embed, React runs inside a shadow tree; `event.target` is retargeted to the host
 * when listening on `document`, so `container.contains(event.target)` is always false and
 * click-outside closes the export menu on mousedown before the menu item's click runs — no export API.
 */
function composedPathContains(event: MouseEvent, container: HTMLElement | null): boolean {
  if (!container) {
    return false;
  }
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
  return path.some((node) => node instanceof Node && container.contains(node));
}

/** Table skeleton while loading — same layout as Plans of study (`plans-of-study.tsx` showSkeleton branch). */
const COURSE_CATALOG_SKELETON_COLUMNS = [
  '22%',
  '9%',
  '12%',
  '12%',
  '10%',
  '8%',
  '9%',
  '8%',
] as const;
const COURSE_CATALOG_SKELETON_ROW_COUNT = 8;

export const CourseCatalog = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, closeToastById } = useToastNotifications();
  const gridRef = useRef<AgGridReact<Course>>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLength, setPageLength] = useState(DEFAULT_COURSE_CATALOG_PAGE_SIZE);
  const [paginatedRows, setPaginatedRows] = useState<Course[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<Set<string>>(new Set());
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownContainerRef = useRef<HTMLDivElement>(null);
  const [showBulkActionsDropdown, setShowBulkActionsDropdown] = useState(false);
  const bulkActionsDropdownContainerRef = useRef<HTMLDivElement>(null);
  const [showBulkChangeStatusModal, setShowBulkChangeStatusModal] = useState(false);
  const [showBulkChangeSubjectAreaModal, setShowBulkChangeSubjectAreaModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const isDistrictLevel = !isCourseCatalogReadOnly();
  const canExportCourseMapping = canExportCourseMappingFromCpContext();

  const catalogFilters = useCourseCatalogFilters();
  const { drawerState } = catalogFilters;

  const filters: CourseFilters = useMemo(
    () => ({
      ...catalogFilters.queryFilters,
      page: currentPage,
      pageSize: pageLength,
      sortBy,
      sortOrder,
    }),
    [catalogFilters.queryFilters, currentPage, pageLength, sortBy, sortOrder]
  );

  const { data, isLoading, error, isFetching, isPlaceholderData } = useGetCourseCatalog(filters);
  const showSkeleton = isLoading && !data && !error;
  const { data: schoolsData, isLoading: isSchoolsLoading } = useGetSchools();
  const [mappingDrawerCourse, setMappingDrawerCourse] = useState<Course | null>(null);
  const [mappingFromNewCourse, setMappingFromNewCourse] = useState<{
    id: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    const state = location.state as { openMappingForCourse?: { id: string; name: string } } | null;
    if (state?.openMappingForCourse) {
      setMappingFromNewCourse(state.openMappingForCourse);
      // Clear from history so refreshing the page doesn't reopen the drawer
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const exportCourses = useExportCourses();
  const bulkUpdateStatus = useBulkUpdateCourseStatus();
  const bulkUpdateSubjectArea = useBulkUpdateSubjectArea();
  const bulkDeleteCourses = useBulkDeleteCourses();
  const deleteCourse = useDeleteCourse();
  const fetchAllCourseIds = useFetchAllCourseIds();

  // Redraw the custom select-all header when fetch-all-IDs pending state changes so NeonLoadingIndicator shows.
  useEffect(() => {
    if (!isDistrictLevel) {
      return;
    }
    gridRef.current?.api?.refreshHeader();
  }, [isDistrictLevel, fetchAllCourseIds.isPending]);

  // Reset to first page when filters that affect result size change
  useEffect(() => {
    setCurrentPage(1);
  }, [catalogFilters.queryFilters.search, catalogFilters.hideInactive, pageLength]);

  // Clamp currentPage to available totalPages when new data arrives
  useEffect(() => {
    const totalPages = data?.totalPages;
    if (typeof totalPages === 'number' && totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [data?.totalPages, currentPage]);

  useEffect(() => {
    if (!data?.data) {
      setPaginatedRows([]);
      return;
    }
    setPaginatedRows(data.data);
  }, [data?.data]);

  // Clear selection only when filter criteria change (not on every render; exclude page/sort so page change keeps selection)
  const filterClearKey = useMemo(
    () =>
      JSON.stringify({
        search: catalogFilters.queryFilters.search,
        hideInactive: catalogFilters.hideInactive,
        schools: catalogFilters.queryFilters.schools,
        subjectAreas: catalogFilters.queryFilters.subjectAreas,
        grades: catalogFilters.queryFilters.grades,
      }),
    [
      catalogFilters.queryFilters.search,
      catalogFilters.hideInactive,
      catalogFilters.queryFilters.schools,
      catalogFilters.queryFilters.subjectAreas,
      catalogFilters.queryFilters.grades,
    ]
  );
  const prevFilterClearKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevFilterClearKeyRef.current !== filterClearKey) {
      prevFilterClearKeyRef.current = filterClearKey;
      setSelectedCourses(new Set());
    }
  }, [filterClearKey]);

  const triggerBlobDownload = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const onGridReady = useCallback((params: GridReadyEvent): void => {
    params.api.applyColumnState({
      state: [{ colId: 'id', sort: 'asc' }],
      defaultState: { sort: null },
    });
  }, []);

  const handleGridSortChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    const state = api.getColumnState();
    const sorted = state.filter((s) => s.sort !== null && s.sort !== undefined);
    if (sorted.length > 0) {
      const first = sorted[0];
      setSortBy(first.colId ?? 'id');
      setSortOrder(first.sort === 'asc' ? 'asc' : 'desc');
    } else {
      // Sort cleared (e.g. cycled through null) — reset to default: Course ID ascending
      setSortBy('id');
      setSortOrder('asc');
      api.applyColumnState({
        state: [{ colId: 'id', sort: 'asc' }],
        defaultState: { sort: null },
      });
    }
    setCurrentPage(1);
  }, []);

  const openAddModal = useCallback((): void => {
    navigate(`/${STAFF_ROUTES.courseCatalog.add}`);
  }, [navigate]);

  const handleImportCourses = useCallback((): void => {
    window.open(getImportCoursesUrl(), '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    if (!showExportDropdown) {
      return;
    }
    const handleClickOutside = (e: MouseEvent): void => {
      if (!composedPathContains(e, exportDropdownContainerRef.current)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportDropdown]);

  useEffect(() => {
    if (!showBulkActionsDropdown) {
      return;
    }
    const handleClickOutside = (e: MouseEvent): void => {
      if (!composedPathContains(e, bulkActionsDropdownContainerRef.current)) {
        setShowBulkActionsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBulkActionsDropdown]);

  const handleExportOption = useCallback(
    (option: string): void => {
      setShowExportDropdown(false);
      const type = option === 'catalog' ? 'catalog' : 'mapping';
      const heading =
        type === 'catalog' ? EXPORT_TOAST.ExportingCourseData : EXPORT_TOAST.ExportingCourseMapping;
      const toastIdPromise = showToast('info', { heading }, EXPORT_INFO_TOAST_OPTIONS);
      const schools =
        type === 'mapping' ? schoolsPayloadForMappingExport(schoolsData ?? []) : undefined;
      exportCourses.mutate(
        { type, schools },
        {
          onSuccess: (blob) => {
            void toastIdPromise?.then((id) => id && closeToastById(id));
            triggerBlobDownload(
              blob,
              type === 'catalog'
                ? EXPORT_CATALOG_DOWNLOAD_FILENAME
                : EXPORT_MAPPING_DOWNLOAD_FILENAME
            );
          },
          onError: () => {
            void toastIdPromise?.then((id) => id && closeToastById(id));
            showToast(
              'error',
              { heading: EXPORT_TOAST.UnableToExport },
              EXPORT_ERROR_TOAST_OPTIONS
            );
          },
        }
      );
    },
    [showToast, closeToastById, exportCourses, triggerBlobDownload, schoolsData]
  );

  const { data: subjectAreasData } = useGetSubjectAreas();

  const subjectAreasList = useMemo(() => subjectAreasData?.data ?? [], [subjectAreasData?.data]);

  const subjectAreaOptions = useMemo(() => {
    if (!subjectAreasData?.data) {
      return [];
    }
    return subjectAreasData.data.map((sa) => ({
      id: toSelectorSafeOptionId(subjectAreaToOptionValue(sa)),
      name: sa.name,
      labelText: sa.name,
    }));
  }, [subjectAreasData?.data]);

  const schoolOptions = useMemo(() => {
    if (!schoolsData?.length) {
      return [];
    }
    return schoolsData
      .map((s) => ({ id: s.nid, name: s.nid, labelText: s.name }))
      .sort((a, b) => a.labelText.localeCompare(b.labelText));
  }, [schoolsData]);

  const gradeOptionsForFilter = useMemo(
    () =>
      GRADE_OPTIONS.map((g) => ({
        id: g.value,
        name: g.value,
        labelText: g.text,
      })),
    []
  );

  // ---------------------------------------------------------------------------
  // Filter (same structure as POS: filterFields, filterChips, handlers → DataGrid/FilterPanel)
  // ---------------------------------------------------------------------------
  const handleFilterFieldChange = useCallback(
    (fieldId: string, value: unknown) => {
      if (fieldId === 'subjectAreas') {
        const selectedValues = Array.isArray(value) ? value : [];
        const selectedNames = selectedValues
          .map((selected) => {
            if (typeof selected !== 'string') {
              return undefined;
            }
            const option = subjectAreaOptions.find(
              (opt) => opt.id === selected || opt.name === selected
            );
            return option?.name ?? selected;
          })
          .filter((name): name is string => typeof name === 'string');

        drawerState.setPendingFilter('subjectAreas', selectedNames);
        return;
      }

      drawerState.setPendingFilter(
        fieldId as keyof typeof drawerState.pendingFilters,
        value as string[]
      );
    },
    [drawerState, subjectAreaOptions]
  );

  const handleFilterApply = useCallback(() => {
    catalogFilters.handleApply();
  }, [catalogFilters]);

  const filterFields = useMemo<FilterField[]>(
    () => [
      {
        type: 'multiselect',
        id: 'schools',
        label: 'Schools',
        value: (() => {
          const hsId = getCpContextHighschoolId();
          return !isDistrictLevel && hsId ? [hsId] : drawerState.pendingFilters.schools;
        })(),
        placeholder: 'All schools',
        options: schoolOptions,
        disabled: !isDistrictLevel,
      },
      {
        type: 'multiselect',
        id: 'subjectAreas',
        label: 'Subject areas',
        value: drawerState.pendingFilters.subjectAreas,
        placeholder: 'All subject areas',
        options: subjectAreaOptions,
      },
      {
        type: 'chipMultiselect',
        id: 'grades',
        label: 'Grade Level',
        value: drawerState.pendingFilters.grades,
        options: gradeOptionsForFilter,
      },
    ],
    [
      drawerState.pendingFilters,
      schoolOptions,
      subjectAreaOptions,
      gradeOptionsForFilter,
      isDistrictLevel,
    ]
  );

  const filterChips = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = [];
    const pending = drawerState.pendingFilters;
    for (const schoolId of pending.schools) {
      const opt = schoolOptions.find((o) => o.id === schoolId || o.name === schoolId);
      chips.push({
        id: `school:${schoolId}`,
        label: opt ? `School: ${opt.labelText}` : `School: ${schoolId}`,
      });
    }
    for (const saId of pending.subjectAreas) {
      const opt = subjectAreaOptions.find((o) => o.id === saId || o.name === saId);
      chips.push({
        id: `subjectArea:${saId}`,
        label: opt ? `Subject area: ${opt.labelText}` : `Subject area: ${saId}`,
      });
    }
    for (const gradeVal of pending.grades) {
      const opt = gradeOptionsForFilter.find((o) => o.id === gradeVal || o.name === gradeVal);
      chips.push({
        id: `grade:${gradeVal}`,
        label: opt ? `Grade: ${opt.labelText}` : `Grade: ${gradeVal}`,
      });
    }
    return chips;
  }, [drawerState.pendingFilters, schoolOptions, subjectAreaOptions, gradeOptionsForFilter]);

  const handleChipRemove = useCallback(
    (chipId: string) => catalogFilters.handleChipRemove(chipId),
    [catalogFilters]
  );

  const handleClearAllChips = useCallback(() => catalogFilters.handleClearAll(), [catalogFilters]);

  const handleFilterApplyAndClose = useCallback(() => {
    handleFilterApply();
  }, [handleFilterApply]);

  const handleBulkActionOption = useCallback((option: string): void => {
    setShowBulkActionsDropdown(false);
    if (option === 'delete') {
      setShowBulkDeleteModal(true);
    } else if (option === 'update-status') {
      setShowBulkChangeStatusModal(true);
    } else if (option === 'update-subject-area') {
      setShowBulkChangeSubjectAreaModal(true);
    }
  }, []);

  const handleBulkStatusSave = useCallback(
    (status: string) => {
      const ids = Array.from(selectedCourses);
      bulkUpdateStatus.mutate(
        { courses: ids, status },
        {
          onSuccess: () => {
            const statusLabel =
              COURSE_STATUS_OPTIONS.find((o) => o.value === status)?.text ?? status;
            showToast('success', {
              heading: `${BULK_ACTION_TOAST.StatusSuccessPrefix} ${statusLabel.toLowerCase()}`,
            });
            setShowBulkChangeStatusModal(false);
            setSelectedCourses(new Set());
          },
          onError: () => showToast('error', { heading: BULK_ACTION_TOAST.StatusError }),
        }
      );
    },
    [selectedCourses, bulkUpdateStatus, showToast]
  );

  const handleBulkSubjectAreaSave = useCallback(
    (subjectAreaOptionValue: string) => {
      const sa = findSubjectAreaByOptionValue(subjectAreasList, subjectAreaOptionValue);
      if (!sa) {
        return;
      }
      const ids = Array.from(selectedCourses);
      bulkUpdateSubjectArea.mutate(
        { courses: ids, subjectArea: sa.name },
        {
          onSuccess: () => {
            showToast('success', { heading: BULK_ACTION_TOAST.SubjectAreaSuccess });
            setShowBulkChangeSubjectAreaModal(false);
            setSelectedCourses(new Set());
          },
          onError: () => showToast('error', { heading: BULK_ACTION_TOAST.SubjectAreaError }),
        }
      );
    },
    [selectedCourses, bulkUpdateSubjectArea, showToast, subjectAreasList]
  );

  const handleBulkDeleteConfirm = useCallback(() => {
    const ids = Array.from(selectedCourses);
    bulkDeleteCourses.mutate(ids, {
      onSuccess: () => {
        showToast('success', { heading: BULK_ACTION_TOAST.DeleteSuccess });
        setShowBulkDeleteModal(false);
        setSelectedCourses(new Set());
      },
      onError: () => showToast('error', { heading: BULK_ACTION_TOAST.DeleteError }),
    });
  }, [selectedCourses, bulkDeleteCourses, showToast]);

  const openViewCourse = useCallback(
    (course: Course): void => {
      navigate(`/${STAFF_ROUTES.courseCatalog.view(course.id)}`);
    },
    [navigate]
  );

  const openEditModal = useCallback(
    (course: Course): void => {
      navigate(`/${STAFF_ROUTES.courseCatalog.edit(course.id)}`);
    },
    [navigate]
  );

  const openDeleteModal = useCallback((course: Course): void => {
    setCourseToDelete(course);
    setShowDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback((): void => {
    setShowDeleteModal(false);
    setCourseToDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback((): void => {
    if (!courseToDelete) {
      return;
    }
    deleteCourse.mutate(courseToDelete.id, {
      onSuccess: () => {
        showToast('success', {
          heading: formatCourseDeletedSuccessHeading(courseToDelete.name),
        });
        setSelectedCourses((prev) => {
          if (!prev.has(courseToDelete.id)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(courseToDelete.id);
          return next;
        });
        closeDeleteModal();
      },
      onError: () => {
        showToast('error', { heading: DELETE_COURSE_TOAST.Error });
      },
    });
  }, [courseToDelete, deleteCourse, showToast, closeDeleteModal]);

  const totalCourses = data?.total ?? 0;
  const hasCourses = totalCourses > 0;

  /**
   * Show the filter/action bar only when:
   * - there ARE courses in the catalog (hasCourses), OR
   * - the user has explicitly applied search/drawer filters (hasExplicitFilters)
   *
   * Per AC: in the TRUE empty state (zero courses, no explicit filters), the following
   * must NOT be shown: Filtering, Searching, Bulk actions, Export, [# courses], Hide Inactive.
   *
   * Use debounced `queryFilters.search` (not raw `searchTerm`) so clearing the search box does not
   * briefly claim "no filters" while the API query still runs / `keepPreviousData` still shows the
   * previous empty search — that mismatch flashed the global "no courses" empty state. Also treat
   * placeholder rows during a refetch as filtered so the grid keeps the filtered empty UI until
   * new data arrives.
   */
  const hasExplicitFilters =
    filterChips.length > 0 ||
    !!catalogFilters.queryFilters.search ||
    !!catalogFilters.searchTerm.trim() ||
    (isFetching && isPlaceholderData);
  const showLayout = hasCourses || hasExplicitFilters;

  const exportDropdownOptions = useMemo(
    () =>
      canExportCourseMapping ? EXPORT_OPTIONS : EXPORT_OPTIONS.filter((o) => o.value === 'catalog'),
    [canExportCourseMapping]
  );

  const courseCatalogSecondaryActions = useMemo(() => {
    if (!hasCourses && !isDistrictLevel) {
      return undefined;
    }
    return (
      <div
        className={`course-catalog-header-actions${
          isDistrictLevel ? '' : ' course-catalog-header-actions--school-user'
        }`}
      >
        {hasCourses && (
          <>
            <div
              ref={exportDropdownContainerRef}
              className="dropdown-container"
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <NeonButton
                id={COURSE_CATALOG_ID.ExportButton}
                dataType={NeonButtonTypes.Secondary}
                dataText={COURSE_CATALOG_TEXT.ExportButton}
                dataIcon={COURSE_CATALOG_ICON.Export}
                dataIconPosition="right"
                dataIsLoading={exportCourses.isPending ? 'true' : undefined}
                disabled={exportCourses.isPending}
                className="cc-export-button"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
              />
              <NeonTooltip dataTargetId={COURSE_CATALOG_ID.ExportButton}>
                <span>
                  {canExportCourseMapping
                    ? COURSE_CATALOG_TEXT.ExportButtonTooltip
                    : COURSE_CATALOG_TEXT.ExportButtonTooltipSchool}
                </span>
              </NeonTooltip>
              {showExportDropdown && (
                <div className="dropdown-menu">
                  {exportDropdownOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleExportOption(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        {isDistrictLevel && (
          <NeonButton
            id={COURSE_CATALOG_ID.ImportButton}
            dataType={NeonButtonTypes.Secondary}
            dataText={COURSE_CATALOG_TEXT.ImportButton}
            dataIcon={COURSE_CATALOG_ICON.Import}
            onClick={handleImportCourses}
          />
        )}
      </div>
    );
  }, [
    isDistrictLevel,
    canExportCourseMapping,
    hasCourses,
    showExportDropdown,
    exportCourses.isPending,
    exportDropdownOptions,
    handleExportOption,
    handleImportCourses,
  ]);

  const clearFiltersOnBreadcrumbLeave = useCallback(() => {
    clearCourseCatalogFilterSession();
  }, []);

  const catalogBreadcrumbs = useMemo(
    () =>
      getCourseCatalogBreadcrumbs({
        onBreadcrumbLeave: clearFiltersOnBreadcrumbLeave,
      }),
    [clearFiltersOnBreadcrumbLeave]
  );

  usePageConfig({
    breadcrumbs: catalogBreadcrumbs,
    breadcrumbsId: COURSE_CATALOG_ID.Breadcrumbs,
    title: COURSE_CATALOG_TEXT.Title,
    action: !isDistrictLevel
      ? undefined
      : {
          id: COURSE_CATALOG_ID.AddButton,
          text: COURSE_CATALOG_TEXT.AddButton,
          icon: COURSE_CATALOG_ICON.Add,
          type: NeonButtonTypes.Primary,
          onClick: openAddModal,
        },
    secondaryActions: courseCatalogSecondaryActions,
    // Include export pending (and dropdown open) so PageHeader picks up spinner visibility —
    // usePageConfig only re-runs setConfig when secondaryActionsKey / serialized config changes.
    secondaryActionsKey: hasCourses
      ? `with-export${showExportDropdown ? '-open' : ''}${isDistrictLevel ? '-import' : ''}${exportCourses.isPending ? '-exporting' : ''}`
      : isDistrictLevel
        ? 'import-only'
        : '',
  });

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedCourses(new Set());
        return;
      }
      if (totalCourses <= 0) {
        return;
      }
      fetchAllCourseIds.mutate(
        { filters, total: totalCourses },
        {
          onSuccess: (ids) => {
            setSelectedCourses(new Set(ids));
          },
          onError: () => showToast('error', { heading: 'Failed to load all course IDs' }),
        }
      );
    },
    [filters, totalCourses, fetchAllCourseIds, showToast]
  );

  const hasSelectedCourses = selectedCourses.size > 0;

  const handleToggleRow = useCallback((id: string) => {
    setSelectedCourses((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleTogglePage = useCallback(
    (ids: string[]) => {
      const allPageSelected = ids.every((id) => selectedCourses.has(id));
      if (allPageSelected) {
        // Header checkbox unchecked → clear all selections (same as "Clear all" button)
        setSelectedCourses(new Set());
      } else {
        // Header checkbox checked → select all courses across all pages
        // (same as "Select all N" button: fetches all IDs from the server)
        handleSelectAll(true);
      }
    },
    [selectedCourses, handleSelectAll]
  );

  // checkboxColumn is only provided to the DataGrid for district-level users
  const checkboxColumn = useMemo<DataGridCheckboxColumn<Course> | undefined>(
    () =>
      isDistrictLevel
        ? {
            getRowId: (row) => row.id,
            selectedIds: selectedCourses,
            onToggleRow: handleToggleRow,
            onTogglePage: handleTogglePage,
            disabled: fetchAllCourseIds.isPending,
            headerSelectAllLoading: fetchAllCourseIds.isPending,
            width: 48,
          }
        : undefined,
    [
      isDistrictLevel,
      selectedCourses,
      handleToggleRow,
      handleTogglePage,
      fetchAllCourseIds.isPending,
    ]
  );

  const gridOptions: GridOptions<Course> = useMemo(
    () => ({
      getRowId: (params) => params.data.id,
      tooltipShowDelay: 500,
      suppressRowClickSelection: true,
      suppressColumnVirtualisation: true,
      maintainColumnOrder: true,
      /* Custom cells use CourseCatalogTruncatingField for ellipsis-only tooltips. */
      enableBrowserTooltips: false,
      suppressCellFocus: true,
      suppressHorizontalScroll: true,
      suppressMovableColumns: true,
      alwaysSingleSort: true,
      /* DataGrid defaults enableCellTextSelection=true, which forces ag-cell-wrapper + extra value
       * handling. After the schools mapping drawer closes, focus returns to the cell and a stray
       * formatted fragment (e.g. "|1|") can appear next to the button. */
      enableCellTextSelection: false,
      defaultColDef: {
        resizable: false,
        sortable: true,
        suppressMovable: true,
        suppressAutoSize: true,
        comparator: (valueA: unknown, valueB: unknown) => {
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return valueA.localeCompare(valueB, undefined, { sensitivity: 'case' });
          }
          if (valueA !== null && valueA !== undefined && valueB !== null && valueB !== undefined) {
            if (valueA < valueB) {
              return -1;
            }
            if (valueA > valueB) {
              return 1;
            }
          }
          return 0;
        },
      },
      onGridReady,
      onSortChanged: handleGridSortChanged,
    }),
    [onGridReady, handleGridSortChanged]
  );

  const columnDefs = useMemo<ColDef<Course>[]>(() => {
    const cellStyle: CellStyle = {
      ...tableColumnsStyle,
    };

    const columns: ColDef<Course>[] = [
      {
        field: 'name',
        headerName: 'Course name',
        flex: 2,
        minWidth: 140,
        headerClass: COURSE_CATALOG_GRID_HEADER_CLASS,
        cellStyle,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
        cellRenderer: (props: { data: Course }) => (
          <CourseCatalogTruncatingField
            variant="button"
            tooltipId={`course-name-${props.data.id}`}
            tooltipText={props.data.name}
            onClick={() => openViewCourse(props.data)}
          >
            {props.data.name}
          </CourseCatalogTruncatingField>
        ),
      },
      {
        field: 'id',
        headerName: 'Course ID',
        flex: 1,
        minWidth: 90,
        headerClass: COURSE_CATALOG_GRID_HEADER_CLASS,
        cellStyle,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
        cellRenderer: (props: { data: Course }) => (
          <CourseCatalogTruncatingField
            variant="span"
            tooltipId={`course-id-${props.data.id}`}
            tooltipText={props.data.id}
          >
            {props.data.id}
          </CourseCatalogTruncatingField>
        ),
      },
      {
        field: 'subjectArea',
        headerName: 'Subject area',
        flex: 1,
        minWidth: 90,
        headerClass: COURSE_CATALOG_GRID_HEADER_CLASS,
        cellStyle,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
        cellRenderer: (props: { data: Course }) => {
          const text = props.data.subjectArea?.name || '-';
          return (
            <CourseCatalogTruncatingField
              variant="span"
              tooltipId={`subject-area-${props.data.id}`}
              tooltipText={text}
            >
              {text}
            </CourseCatalogTruncatingField>
          );
        },
      },
      {
        field: 'grades',
        headerName: 'Grade levels',
        flex: 1,
        minWidth: 90,
        headerClass: COURSE_CATALOG_GRID_HEADER_CLASS_STATIC,
        cellStyle,
        sortable: false,
        cellRenderer: (props: { data: Course }) => {
          const grades = props.data.grades;
          const text = grades && grades.length > 0 ? grades.join(', ') : '-';
          return (
            <CourseCatalogTruncatingField
              variant="span"
              tooltipId={`grade-levels-${props.data.id}`}
              tooltipText={text}
            >
              {text}
            </CourseCatalogTruncatingField>
          );
        },
      },
    ];

    if (isDistrictLevel) {
      columns.push({
        field: 'schools',
        headerName: 'Schools',
        flex: 1,
        minWidth: 70,
        headerClass: COURSE_CATALOG_GRID_HEADER_CLASS,
        cellStyle,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        headerComponent: SortableHeader,
        /* Arrays infer as object data type; AG Grid can surface a formatted value next to the renderer
         * on row focus/selection (reads like "| 1 |"). Use a numeric value + disable inference. */
        cellDataType: false,
        valueGetter: (p) => p.data?.schools?.length ?? 0,
        cellRenderer: (props: { data: Course; value?: unknown }) => {
          const count =
            typeof props.value === 'number' ? props.value : (props.data.schools?.length ?? 0);
          return (
            <button
              type="button"
              className="course-link"
              onClick={() => {
                setMappingDrawerCourse(props.data);
              }}
            >
              {count}
            </button>
          );
        },
      });
    }

    columns.push({
      field: 'credits',
      headerName: 'Credits',
      flex: 1,
      minWidth: 70,
      headerClass: COURSE_CATALOG_GRID_HEADER_CLASS,
      cellStyle,
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      headerComponent: SortableHeader,
    });

    columns.push({
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 90,
      headerClass: COURSE_CATALOG_GRID_HEADER_CLASS,
      cellStyle: { ...cellStyle, display: 'flex', alignItems: 'center', gap: '6px' },
      sortable: true,
      sortingOrder: ['asc', 'desc'],
      headerComponent: SortableHeader,
      cellRenderer: (props: { data: Course }) => {
        const isActive = props.data.status === CourseStatus.ACTIVE;
        return (
          <span className="status-cell">
            {isActive ? (
              <NeonIcon
                dataIcon="status-active"
                dataSize="small"
                style={{ verticalAlign: 'sub' }}
              />
            ) : (
              <NeonIcon
                dataIcon="status-inactive"
                dataSize="small"
                style={{ verticalAlign: 'sub' }}
              />
            )}
            <span>{isActive ? 'Active' : 'Inactive'}</span>
          </span>
        );
      },
    });

    if (isDistrictLevel) {
      columns.push({
        colId: 'actions',
        headerName: 'Actions',
        headerClass: COURSE_CATALOG_GRID_HEADER_CLASS_STATIC,
        cellStyle: { ...tableColumnsStyle, display: 'flex', alignItems: 'center', gap: '4px' },
        sortable: false,
        minWidth: 128,
        width: 128,
        cellRenderer: (props: { data: Course }) => (
          <div className="action-buttons">
            <NeonButton
              id={`edit-${props.data.id}`}
              dataIcon="edit"
              dataType="utility"
              dataAriaLabel={`Edit ${props.data.name}`}
              dataText="Edit"
              dataShowText="false"
              onClick={() => openEditModal(props.data)}
            />
            <NeonTooltip dataTargetId={`edit-${props.data.id}`}>
              <span>Edit</span>
            </NeonTooltip>
            <NeonButton
              id={`delete-${props.data.id}`}
              dataIcon="delete"
              dataType="utility"
              dataAriaLabel={`Delete ${props.data.name}`}
              dataText="Delete"
              dataShowText="false"
              onClick={() => openDeleteModal(props.data)}
            />
            <NeonTooltip dataTargetId={`delete-${props.data.id}`}>
              <span>Delete</span>
            </NeonTooltip>
          </div>
        ),
      });
    }

    return columns;
  }, [isDistrictLevel, openDeleteModal, openEditModal, openViewCourse, setMappingDrawerCourse]);

  const showPaginationBar = totalCourses > COURSE_CATALOG_PAGINATION_THRESHOLD;

  if (showSkeleton) {
    return (
      <div className="course-catalog-container">
        <div style={sectionSpacingStyle}>
          <div className="course-catalog-body">
            <div
              className="course-catalog-content"
              style={skeletonContainerStyle}
              data-testid="course-catalog-skeleton"
              aria-busy="true"
              aria-label="Loading course catalog"
            >
              <div style={skeletonHeaderStyle}>
                {COURSE_CATALOG_SKELETON_COLUMNS.map((w, i) => (
                  <div key={`cc-sh-${i}`} style={skeletonBarStyle(w)} />
                ))}
              </div>
              {Array.from({ length: COURSE_CATALOG_SKELETON_ROW_COUNT }).map((_, ri) => (
                <div key={`cc-sr-${ri}`} style={skeletonRowStyle}>
                  {COURSE_CATALOG_SKELETON_COLUMNS.map((w, ci) => (
                    <div key={`cc-sc-${ri}-${ci}`} style={skeletonBarStyle(w)} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="course-catalog-container">
      <div style={sectionSpacingStyle}>
        <div className="course-catalog-body">
          {error && (
            <NeonCardStandard>
              <div data-slot={DATA_SLOT} style={EMPTY_STATE_CARD_STYLE}>
                <NeonErrorPageContent dataErrorCode={'500'} />
              </div>
            </NeonCardStandard>
          )}
          {!error && (
            <DataGrid<Course>
              id="course-catalog"
              columnDefs={columnDefs}
              rowData={paginatedRows}
              domLayout="normal"
              gridHeight="min(600px, 60vh)"
              rowHeight={50}
              headerHeight={50}
              cardWrapper={true}
              childrenInsideCard={true}
              checkboxColumn={checkboxColumn}
              emptyRowsTreatAsFiltered={hasExplicitFilters}
              emptyState={
                <EmptyState
                  title={EMPTY_COURSE_CATALOG_TEXT.Title}
                  body={EMPTY_COURSE_CATALOG_TEXT.Body}
                  {...(isDistrictLevel && {
                    buttonText: EMPTY_COURSE_CATALOG_TEXT.Button,
                    buttonId: EMPTY_COURSE_CATALOG_ID.Button,
                    onAction: openAddModal,
                  })}
                />
              }
              emptyStateWhenFiltered={
                <EmptyState
                  title={EMPTY_COURSE_CATALOG_TEXT.NoFilterResultsTitle}
                  body={EMPTY_COURSE_CATALOG_TEXT.NoFilterResultsBody}
                />
              }
              className="neon-data-grid course-catalog-grid"
              gridOptions={gridOptions}
              gridRef={gridRef}
              filter={
                showLayout
                  ? {
                      title: COURSE_CATALOG_TEXT.FiltersButton,
                      fields: filterFields,
                      chips: filterChips,
                      filterCount: filterChips.length,
                      hasChanges: drawerState.hasChanges,
                      onFieldChange: handleFilterFieldChange,
                      onChipRemove: handleChipRemove,
                      onClearAllChips: handleClearAllChips,
                      onApply: handleFilterApplyAndClose,
                      applyText: COURSE_CATALOG_TEXT.FilterApply,
                      closeButtonId: COURSE_CATALOG_ID.FilterCancelButton,
                      applyButtonId: COURSE_CATALOG_ID.FilterApplyButton,
                      toggleButtonId: COURSE_CATALOG_ID.FiltersButton,
                      toggleButtonText: COURSE_CATALOG_TEXT.FiltersButton,
                      toggleButtonIcon: COURSE_CATALOG_ICON.Filters,
                    }
                  : undefined
              }
              actionBarLeftSupplement={
                showLayout ? (
                  <NeonCheckboxSingle
                    id={COURSE_CATALOG_ID.HideInactiveCheckbox}
                    dataLabelText={COURSE_CATALOG_TEXT.HideInactiveCourses}
                    value={catalogFilters.hideInactive}
                    onInput={(e: Event) => {
                      const t = e.target as HTMLInputElement & { getValue?: () => boolean };
                      catalogFilters.setHideInactive(t?.getValue?.() ?? t?.checked ?? false);
                    }}
                  />
                ) : undefined
              }
              actionBar={
                showLayout ? (
                  <>
                    <span className="course-count">
                      {totalCourses} {COURSE_CATALOG_TEXT.CoursesCount}
                    </span>
                    <div className="search-container">
                      <NeonIcon dataIcon="search" className="search-icon" aria-hidden="true" />
                      <input
                        id={COURSE_CATALOG_ID.SearchInput}
                        type="text"
                        placeholder={COURSE_CATALOG_TEXT.SearchPlaceholder}
                        value={catalogFilters.searchTerm}
                        onChange={(e) => catalogFilters.setSearchTerm(e.target.value)}
                        className="course-search-input"
                      />
                    </div>
                    {isDistrictLevel && (
                      <div
                        ref={bulkActionsDropdownContainerRef}
                        className="dropdown-container course-catalog-bulk-actions"
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <NeonButton
                          id={COURSE_CATALOG_ID.BulkActionsButton}
                          dataType={
                            hasSelectedCourses
                              ? NeonButtonTypes.Secondary
                              : NeonButtonTypes.Tertiary
                          }
                          dataText={COURSE_CATALOG_TEXT.BulkActionsButton}
                          dataIcon="caret-down"
                          dataIconPosition="right"
                          disabled={!hasSelectedCourses}
                          onClick={() => setShowBulkActionsDropdown(!showBulkActionsDropdown)}
                        />
                        {showBulkActionsDropdown && hasSelectedCourses && (
                          <div className="dropdown-menu">
                            {BULK_ACTION_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                className="dropdown-item"
                                onClick={() => handleBulkActionOption(opt.value)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : undefined
              }
            >
              {showPaginationBar && (
                <form
                  onSubmit={(e) => e.preventDefault()}
                  style={{ display: 'contents' }}
                  className="course-catalog-pagination-wrapper"
                >
                  <div
                    onClickCapture={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <NeonPagination
                      id="course-catalog-pagination"
                      dataArrayLength={totalCourses}
                      dataCurrentPage={currentPage}
                      dataPageLength={pageLength}
                      dataPageSizeOptions="25,50,100,200"
                      dataShowJumpTo={true}
                      dataShowResults={true}
                      neonPaginationChange={(newPage, newPageLength) => {
                        setCurrentPage(newPage);
                        setPageLength(newPageLength);
                      }}
                    />
                  </div>
                </form>
              )}
            </DataGrid>
          )}
        </div>
      </div>

      <BulkChangeStatusModal
        isOpen={showBulkChangeStatusModal}
        selectedCount={selectedCourses.size}
        onSave={handleBulkStatusSave}
        onClose={() => setShowBulkChangeStatusModal(false)}
        isSaving={bulkUpdateStatus.isPending}
      />
      <BulkChangeSubjectAreaModal
        isOpen={showBulkChangeSubjectAreaModal}
        selectedCount={selectedCourses.size}
        subjectAreas={subjectAreasList}
        onSave={handleBulkSubjectAreaSave}
        onClose={() => setShowBulkChangeSubjectAreaModal(false)}
        isSaving={bulkUpdateSubjectArea.isPending}
      />
      <BulkDeleteCoursesModal
        isOpen={showBulkDeleteModal}
        selectedCount={selectedCourses.size}
        onConfirm={handleBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteModal(false)}
        isDeleting={bulkDeleteCourses.isPending}
      />
      <DeleteCourseModal
        isOpen={showDeleteModal}
        courseName={courseToDelete?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onClose={closeDeleteModal}
        isDeleting={deleteCourse.isPending}
      />
      {mappingDrawerCourse && (
        <CourseSchoolsMappingDrawer
          courseId={mappingDrawerCourse.id}
          courseName={mappingDrawerCourse.name}
          currentSchoolNids={mappingDrawerCourse.schools ?? []}
          allSchools={schoolsData ?? []}
          isSchoolsLoading={isSchoolsLoading}
          onClose={() => setMappingDrawerCourse(null)}
        />
      )}
      {mappingFromNewCourse && (
        <CourseSchoolsMappingDrawer
          courseId={mappingFromNewCourse.id}
          courseName={mappingFromNewCourse.name}
          currentSchoolNids={[]}
          allSchools={schoolsData ?? []}
          isSchoolsLoading={isSchoolsLoading}
          onClose={() => setMappingFromNewCourse(null)}
        />
      )}
    </div>
  );
};
